import { ActionTree, GetterTree, MutationTree } from 'vuex';
import { LoggingHelper, LogLevel } from 'core-helpers';
import QueuesService from '@/services/QueuesService';
import SlotsService from '@/services/SlotsService';
import Queue from '@/models/Queue';
import Slot from '@/models/Slot';
// import { parseQueue } from '@/common/helpers/parseQueue';
import { User } from '@/models/User';
import SlotsGroup from '@/models/SlotsGroup';
import { RootState } from '../types';
import { ActionTypes, FetchSlotsForQueueData, GetterTypes, MutationTypes, QueueState } from './types';

const namespaced: boolean = true;

const queuesService: QueuesService = new QueuesService();
const slotsService: SlotsService = new SlotsService();

const state: QueueState = {
  queues: [],
  queuesLoading : true,
  userQueues: [],
  queue: {} as Queue,
  queueUuid: '',
  isLoading: false
};

const getters: GetterTree<QueueState, RootState> = {
  [GetterTypes.GET_QUEUES](state): Queue[] {
    return state.queues;
  },
  [GetterTypes.GET_QUEUES_LOADING](state): boolean {
    return state.queuesLoading;
  },
  [GetterTypes.GET_USER_QUEUES](state): Queue[] {
    return state.userQueues;
  },
  [GetterTypes.GET_QUEUE](state): Queue {
    return state.queue;
  },
  [GetterTypes.GET_SELECTED_QUEUE_UUID](state): string {
    return state.queueUuid;
  },
  [GetterTypes.GET_IS_LOADING](state): boolean {
    return state.isLoading;
  }
};

const actions: ActionTree<QueueState, RootState> = {
  async [ActionTypes.FETCH_QUEUES]({ commit }, scope: number | null = null): Promise<void> {
    try {
      commit(MutationTypes.SET_QUEUES_LOADING, true);
      const data = await queuesService.fetchQueues(scope);
      commit(MutationTypes.SET_QUEUES, data);
      commit(MutationTypes.SET_QUEUES_LOADING, false);
    } catch (err: unknown) {
      LoggingHelper.log(err, LogLevel.ERROR);
      commit(MutationTypes.SET_QUEUES_LOADING, false);
      throw err;
    }
  },
  async [ActionTypes.FETCH_USER_QUEUES]({ commit }, user: User): Promise<void> {
    try {
      if (user?.uuid) {
        const data: Queue[] = await queuesService.fetchUserQueues();

        commit(MutationTypes.SET_USER_QUEUES, data);
      }
    } catch (err: unknown) {
      LoggingHelper.log(err, LogLevel.ERROR);
      throw err;
    }
  },
  async [ActionTypes.FETCH_QUEUE]({ commit }, payload: string): Promise<void> {
    try {
      const data = await queuesService.fetchQueue(payload);
      commit(MutationTypes.SET_QUEUE, data);
      commit(MutationTypes.SET_SELECTED_QUEUE_UUID, payload);
    } catch (err: unknown) {
      LoggingHelper.log(err, LogLevel.ERROR);
      throw err;
    }
  },
  async [ActionTypes.FETCH_SLOTS_FOR_QUEUE]({ commit }, payload: FetchSlotsForQueueData): Promise<void> {
    commit(MutationTypes.SET_IS_LOADING, true);
    try {
      const data = await slotsService.fetchSlotsForQueue(payload.queueUuid, payload.scope);
      commit(MutationTypes.SET_SLOTS_FOR_QUEUE, data);
    } catch (err: unknown) {
      LoggingHelper.log(err, LogLevel.ERROR);
      throw err;
    }
    commit(MutationTypes.SET_IS_LOADING, false);
  },
  async [ActionTypes.ENROLL_TO_SLOT]({ commit }, payload: string): Promise<void> {
    const data = await slotsService.enrollToSlot(payload);
    commit(MutationTypes.SET_SLOT_FOR_USER_QUEUES_AFTER_ENROLL, data);
  },
  async [ActionTypes.SIGN_OFF_FROM_SLOT]({ commit }, payload: string): Promise<void> {
    try {
      const data = await slotsService.signOffFromSlot(payload);

      commit(MutationTypes.SET_SLOT_AFTER_SIGN_OFF, data);
    } catch (err: unknown) {
      LoggingHelper.log(err, LogLevel.ERROR);
      throw err;
    }
  },
  [ActionTypes.SELECT_QUEUE]({ commit }, queue: Queue): void {
    commit(MutationTypes.SET_QUEUE, queue);
    commit(MutationTypes.SET_SELECTED_QUEUE_UUID, queue.uuid);
  },
  [ActionTypes.CLEAR_SELECTED_QUEUE]({ commit }): void {
    commit(MutationTypes.SET_QUEUE, {} as Queue);
    commit(MutationTypes.SET_SELECTED_QUEUE_UUID, '');
  },
  [ActionTypes.REMOVE_SLOT]({ commit, state }, slotUuid: string): void {
    if (state.queue) {
      const { queue } = state;
      queue.slots = queue.slots.filter((slot: Slot) => slot.uuid !== slotUuid);
      const { days } = queue;

      if (days) {
        days.map((slotsGroup: SlotsGroup) => {
          slotsGroup.slots = slotsGroup.slots.filter((slot: Slot) => slot.uuid !== slotUuid);
          return slotsGroup;
        });
      }

      commit(MutationTypes.SET_QUEUE, queue);
    }
  },
  [ActionTypes.MERGE_TO_QUEUE]({ state }, queue: Queue): void {
    state.queues.map((q: Queue) => {
      if (q.uuid === queue.uuid) {
        q = queue;
      }

      return q;
    });
  }
};

const mutations: MutationTree<QueueState> = {
  [MutationTypes.SET_IS_LOADING](state, payload: boolean): void {
    state.isLoading = payload;
  },
  [MutationTypes.SET_QUEUES_LOADING](state, payload: boolean): void {
    state.queuesLoading = payload;
  },
  [MutationTypes.SET_QUEUES](state, payload): void {
    state.queues = payload;
  },
  [MutationTypes.SET_USER_QUEUES](state, payload): void {
    state.userQueues = payload;
  },
  [MutationTypes.SET_QUEUE](state, queue: Queue): void {
    state.queue = queue;
  },
  [MutationTypes.SET_SLOTS_FOR_QUEUE](state, payload: Queue): void {
    state.queue = payload;
    // state.queue = parseQueue(state.queue);
  },
  [MutationTypes.SET_SELECTED_QUEUE_UUID](state, uuid: string): void {
    state.queueUuid = uuid;
  },
  [MutationTypes.SET_SLOT_FOR_USER_QUEUES_AFTER_ENROLL](state, payload: Slot): void {
    const isQueueInUserQueues: boolean = state.userQueues.some((queue: Queue) => queue.uuid === payload.queueUuid);

    if (isQueueInUserQueues) {
      state.userQueues.map((queue: Queue) => {
        if (queue.slots === null) {
          queue.slots = [];
        }

        if (queue.uuid === payload.queueUuid) {
          queue.slots.push(payload);
        }

        return queue;
      });
    } else {
      const newQueue: Queue | undefined = state.queues.find((queue: Queue) => queue.uuid === payload.queueUuid);

      if (newQueue) {
        newQueue.slots = [];
        newQueue.slots.push(payload);

        state.userQueues.push(newQueue);
      }
    }
  },
  [MutationTypes.SET_SLOT_AFTER_SIGN_OFF](state, payload: Slot): void {
    state.userQueues.map((queue: Queue) => {
      if (queue.uuid === payload.queueUuid) {
        queue.slots = queue.slots.filter((slot: Slot) => slot.uuid !== payload.uuid);
      }

      return queue;
    });

    state.userQueues = state.userQueues.filter((queue: Queue) => queue?.slots?.length !== 0);
  }
};

export default {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
